import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { MdOutlineTimer } from 'react-icons/md';
import useAvailableServicesModal from '../../hooks/useAvailableServicesModal';
import { setCurrentPartnerBusinessService } from '../../redux/ducks/PartnerSelectionDucks';
import type { PartnerServiceDescriptor } from '../../redux/ducks/SearchDucks.types';
import { setAvailableServicesModalOpen } from '../../redux/ducks/UIDucks';
import type { RootReducerType } from '../../redux/reducers/RootReducer.types';
import Modal from './Modal';

const AvailableServicesModal = () => {
  const availableServicesModal = useAvailableServicesModal();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const partnerProfile = useSelector(
    (state: RootReducerType) => state.search.getPartnerDetailsResponse?.data,
  );

  const currentPartnerBusiness = useSelector(
    (state: RootReducerType) => state.partnerSelection.currentPartnerBusiness,
  );

  const currentPartnerService = useSelector(
    (state: RootReducerType) =>
      state.partnerSelection.currentPartnerBusinessService,
  );

  const [previewService, setPreviewService] = useState<
    PartnerServiceDescriptor | undefined
  >();

  const onSelectedService = (service: PartnerServiceDescriptor) => {
    setPreviewService(service);
  };

  const onSubmit = () => {
    if (previewService) {
      dispatch(setCurrentPartnerBusinessService(previewService));
    }
    availableServicesModal.onClose();
  };

  const sortedServices = useMemo<PartnerServiceDescriptor[]>(() => {
    if (partnerProfile && currentPartnerBusiness) {
      const legitServices = partnerProfile.services.filter((s) =>
        currentPartnerBusiness.services.includes(s.id),
      );

      return [...legitServices];
    }

    return [];
  }, [partnerProfile, currentPartnerBusiness]);

  const bodyContent = (
    <div className="flex flex-col gap-4">
      <div className="mb-4 flex w-full flex-col items-center justify-center gap-2">
        {sortedServices?.map((service) => {
          let selected = false;
          if (previewService) {
            selected = service.id === previewService.id;
          } else if (currentPartnerService) {
            selected = service.id === currentPartnerService.id;
          }

          return selected ? (
            <span
              className="flex h-full min-w-full flex-col items-center justify-center rounded-lg border border-solid border-slate-500 bg-slate-500 py-3 text-white drop-shadow-sm"
              data-test-id={`available-service-${service.id}`}
              key={`available-service-${service.id}`}
            >
              <div className="flex h-full min-w-full flex-row">
                <div className="mx-3 flex min-h-full grow flex-col items-center justify-center">
                  <div className="flex min-w-full flex-row items-center justify-between font-bold">
                    <span className="line-clamp-1 grow overflow-hidden text-ellipsis break-all capitalize">
                      {service.name}
                    </span>
                    <span className="line-clamp-1 overflow-hidden text-ellipsis break-all">
                      {new Intl.NumberFormat(i18n.language, {
                        style: 'currency',
                        currency: service.currency,
                      }).format(service.price)}
                    </span>
                  </div>
                  <span className="flex w-full flex-row items-start justify-start">
                    <span className="mr-0.5 flex h-full flex-col items-center justify-center">
                      <MdOutlineTimer size={16} />
                    </span>
                    <span className="line-clamp-1 flex h-full grow flex-col items-start justify-start overflow-hidden text-ellipsis break-all text-sm font-semibold">
                      {t('static.profile_page.service_duration_label', {
                        duration: Math.floor(service.duration / 60),
                      })}
                    </span>
                  </span>
                  {service?.description && (
                    <span className="mt-2 flex w-full flex-col items-start justify-normal text-pretty text-sm text-white">
                      {service.description}
                    </span>
                  )}
                </div>
              </div>
            </span>
          ) : (
            <button
              data-test-id={`available-service-${service.id}`}
              key={`available-service-${service.id}`}
              id={`available-service-${service.id}`}
              aria-label={`available-service-${service.id}`}
              className="flex h-full min-w-full cursor-pointer flex-col items-center justify-center rounded-lg border border-solid border-slate-300 py-3 hover:bg-slate-100"
              type="button"
              onClick={() => onSelectedService(service)}
            >
              <div className="flex h-full min-w-full flex-row">
                <div className="mx-3 flex min-h-full grow flex-col items-center justify-center">
                  <div className="flex min-w-full flex-row items-center justify-between font-bold text-start">
                    <span className="line-clamp-1 grow overflow-hidden text-ellipsis break-all capitalize">
                      {service.name}
                    </span>
                    <span>
                      {new Intl.NumberFormat(i18n.language, {
                        style: 'currency',
                        currency: service.currency,
                      }).format(service.price)}
                    </span>
                  </div>
                  <span className="flex w-full flex-row items-start justify-start">
                    <span className="mr-0.5 flex h-full flex-col items-center justify-center">
                      <MdOutlineTimer size={16} />
                    </span>
                    <span className="line-clamp-1 flex h-full grow flex-col items-start justify-start overflow-hidden text-ellipsis break-all text-sm font-semibold">
                      {t('static.profile_page.service_duration_label', {
                        duration: Math.floor(service.duration / 60),
                      })}
                    </span>
                  </span>
                  {service?.description && (
                    <span className="text-start mt-2 flex w-full flex-col items-start justify-normal text-pretty text-sm text-slate-500">
                      {service.description}
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  useEffect(() => {
    if (!availableServicesModal.isOpen) {
      setPreviewService(undefined);
    }
  }, [availableServicesModal.isOpen]);

  return (
    <Modal
      id="AvailableServicesModal"
      setIsOpen={(open) => dispatch(setAvailableServicesModalOpen(open))}
      disabled={false}
      loading={false}
      isOpen={availableServicesModal.isOpen}
      title={t('static.available_services.modal_title')}
      actionLabel={t('static.available_services.modal_submit_btn_label')}
      onClose={availableServicesModal.onClose}
      onSubmit={onSubmit}
      body={bodyContent}
    />
  );
};

export default AvailableServicesModal;
